/**
 * loginDebug.ts — Utilitário centralizado de diagnóstico do fluxo de login.
 *
 * HOTFIX 2.1: Observabilidade do Login
 *
 * Centraliza todos os logs do login para facilitar debugging remoto.
 * NÃO altera o fluxo de autenticação — apenas observa e registra.
 *
 * Funcionalidades:
 * - logLoginDebug(): log estruturado com requestId, etapa, status, erro, duração
 * - getUserFriendlyError(): mapeia erros técnicos para mensagens amigáveis
 */

// ============================================================
// TIPOS
// ============================================================

/** Etapas do fluxo de login (usadas como valor do campo step nos logs) */
export type LoginStep =
  | 'START'
  | 'SUPABASE_AUTH_START'
  | 'SUPABASE_AUTH_SUCCESS'
  | 'SUPABASE_AUTH_FAIL'
  | 'PROFILE_LOOKUP_START'
  | 'PROFILE_LOOKUP_OK'
  | 'PROFILE_NOT_FOUND'
  | 'PROFILE_INACTIVE'
  | 'EDGE_FUNCTION_START'
  | 'EDGE_FUNCTION_SUCCESS'
  | 'EDGE_FUNCTION_FAIL'
  | 'SESSION_SET'
  | 'NAVIGATE'
  | 'ERROR'
  // HOTFIX 2.2: Limpeza preventiva de sessão na abertura da tela de login
  | 'LOGIN_PAGE_INIT'
  | 'SESSION_CLEAN_START'
  | 'SESSION_CLEAN_SUCCESS'
  | 'SESSION_ALREADY_EMPTY'
  | 'SESSION_CLEAN_ERROR'
  | 'EDGE_FUNCTION_RESPONSE';

/** Dados estruturados para um log de login */
export interface LoginLogData {
  step: LoginStep;
  status?: number;
  errorType?: string;
  message?: string;
  stack?: string;
  durationMs?: number;
}

// ============================================================
// FUNÇÃO CENTRALIZADA DE LOG
// ============================================================

/**
 * Registra um evento do fluxo de login no console.
 *
 * Formato: [LOGIN {requestId abreviado}] STEP={etapa} STATUS={http} TYPE={tipo} MSG="{mensagem}" DURATION={ms}ms
 *
 * @param requestId - Identificador único da tentativa de login (8 primeiros caracteres aparecem no log)
 * @param data - Dados estruturados do evento (step obrigatório, demais opcionais)
 */
export function logLoginDebug(requestId: string, data: LoginLogData): void {
  const shortId = requestId.slice(0, 8);
  const prefix = `[LOGIN ${shortId}]`;
  const parts: string[] = [`STEP=${data.step}`];

  if (data.status !== undefined) parts.push(`HTTP=${data.status}`);
  if (data.errorType) parts.push(`TYPE=${data.errorType}`);
  if (data.message) parts.push(`MSG="${data.message}"`);
  if (data.durationMs !== undefined) parts.push(`DURATION=${data.durationMs}ms`);

  console.log(`${prefix} ${parts.join(' ')}`);

  if (data.stack) {
    console.log(`${prefix} STACK:`, data.stack);
  }
}

// ============================================================
// MAPEAMENTO DE ERROS → MENSAGENS AMIGÁVEIS
// ============================================================

/**
 * Converte um erro técnico (mensagem da Edge Function, exceção de rede, etc.)
 * em uma mensagem amigável para o usuário final.
 *
 * Regras:
 * - "Credenciais invalidas" → "E-mail ou senha incorretos."
 * - "Conta desativada." → mensagem específica de conta desativada
 * - Timeout → "Tempo de conexão excedido."
 * - Erro de rede → "Erro de conexão."
 * - Erro interno → "Erro interno do servidor."
 * - Erro de parsing → "Erro de comunicação."
 * - Erro de migração → mensagem específica
 * - Fallback: preserva a mensagem original (já é suficientemente específica)
 *
 * NUNCA retorna apenas "Credenciais inválidas" como fallback genérico.
 *
 * @param rawMessage - Mensagem de erro original (da Edge Function, exceção, etc.)
 * @returns Mensagem amigável para exibição ao usuário
 */
export function getUserFriendlyError(rawMessage: string): string {
  if (!rawMessage) {
    return 'Erro ao conectar ao servidor. Tente novamente.';
  }

  const msg = rawMessage.toLowerCase();

  // --- Edge Function: credenciais confirmadamente inválidas ---
  if (msg.includes('credenciais invalidas') || msg.includes('invalid login credentials')) {
    return 'E-mail ou senha incorretos.';
  }

  // --- Edge Function: conta desativada ---
  if (msg.includes('conta desativada')) {
    return 'Conta desativada. Entre em contato com o administrador.';
  }

  // --- Edge Function: sem perfil vinculado ---
  if (msg.includes('sem perfil') || msg.includes('usuario sem perfil')) {
    return 'Usuário sem perfil de administrador vinculado.';
  }

  // --- Timeout (AbortController, timeout de fetch) ---
  if (msg.includes('abort') || msg.includes('timeout') || msg.includes('excedido')) {
    return 'Tempo de conexão excedido. Verifique sua internet e tente novamente.';
  }

  // --- Erro de rede (fetch falhou, offline, DNS) ---
  if (msg.includes('failed to fetch') || msg.includes('networkerror') || msg.includes('network error')) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  }

  // --- Erro interno do servidor (500) ---
  if (msg.includes('erro interno') || msg.includes('internal server error')) {
    return 'Erro interno do servidor. Tente novamente em instantes.';
  }

  // --- Erro de parsing / resposta inválida do servidor ---
  if (msg.includes('resposta inválida') || msg.includes('invalid json') || msg.includes('unexpected token')) {
    return 'Erro de comunicação com o servidor. Tente novamente.';
  }

  // --- Erro de migração ---
  if (msg.includes('migrar') || msg.includes('migrada')) {
    return 'Erro ao processar sua conta. Tente novamente.';
  }

  // --- Servidor indisponível (502, 503, manutenção) ---
  if (msg.includes('502') || msg.includes('503') || msg.includes('indisponível') || msg.includes('unavailable') || msg.includes('service unavailable')) {
    return 'Servidor temporariamente indisponível. Tente novamente em instantes.';
  }

  // --- Erro de leitura da resposta ---
  if (msg.includes('erro ao ler resposta')) {
    return 'Erro de comunicação com o servidor. Tente novamente.';
  }

  // --- Erro de rate limiting (429) ---
  if (msg.includes('429') || msg.includes('too many requests') || msg.includes('rate limit')) {
    return 'Muitas tentativas de login. Aguarde alguns instantes e tente novamente.';
  }

  // --- Fallback: a mensagem original já é específica o suficiente ---
  // (ex: "Email e senha sao obrigatorios", "Email not confirmed", etc.)
  return rawMessage;
}